import type { SellerOverviewView, SellerAuthView } from "@/lib/api/types";

/**
 * Should this store be sent to the quick-setup wizard?
 *
 * Pure, and tested, because getting it wrong TRAPS a merchant: the wizard
 * clears its own snapshot when it finishes, so a redirect back into it after
 * completion restarts from question 1 with nothing saved, on every load, with
 * no way out of the portal.
 *
 * The rule is "this store has been configured in NO way at all" — any one of
 * the three answering yes is enough to stay out. It is deliberately an AND of
 * three negatives rather than a completion flag: a store set up before the
 * wizard existed, or through the admin portal, must not be dragged into it.
 */
export function needsQuickSetup(
  overview: SellerOverviewView | undefined | null,
): boolean {
  // No overview yet: not "unconfigured", just unknown. Redirecting on absent
  // data would bounce a merchant mid-load.
  if (!overview) return false;
  return (
    !overview.hasCategories && !canTakeMoney(overview) && !overview.hasProducts
  );
}

/**
 * Can the shop take money at all?
 *
 * COD is a STORE column, not a `StorePaymentOption` row, so
 * `hasActivePaymentOptions` alone answers no for the commonest Egyptian setup
 * — and COD is what the wizard turns on by default. Reading only the rows is
 * what let a merchant finish the wizard and be sent straight back into it.
 */
function canTakeMoney(overview: SellerOverviewView): boolean {
  return overview.hasActivePaymentOptions || overview.acceptsCod;
}

/** Only the store root is a setup entry; its action pages are never redirected. */
export function shouldOpenQuickSetup(
  overview: SellerOverviewView | undefined | null,
  seller: SellerAuthView,
  isDefaultEntry: boolean,
): boolean {
  if (
    !isDefaultEntry ||
    seller.storeAccess !== true ||
    seller.access?.capabilities.onboarding !== true ||
    !seller.permissions.includes("STORE_EDIT")
  )
    return false;
  // Free setup is optional. Product creation changes the dashboard checklist,
  // not admission: Continue and a later reload must not restart onboarding.
  if (seller.access.capabilities.profileBranding !== true) return false;
  return needsQuickSetup(overview);
}
